import { appHref } from '@/utils/appHref';
import { isInAppBrowser } from '@/utils/browserEnv';
/**
 * Client-side memory of the Pass Plan download token.
 *
 * The workbook PDF is gated behind a signed token minted by captureLead and
 * verified by the `/api/pass-plan` serverless route (see aswb-backend
 * passPlanToken.ts). When a visitor captures their email in the diagnostic, we
 * stash the returned token here so the standalone `/pass-plan` page can re-unlock
 * the file WITHOUT a fresh email, even on a later visit or a link with no `?t=`.
 *
 * The token self-expires (30 days) and reveals nothing about the visitor, so
 * plain localStorage is safe; there is no TTL wrapper because the signature is
 * the source of truth on expiry.
 */

const PASS_PLAN_TOKEN_KEY = 'swtpa_pass_plan_token';

/** Persist the download token returned by captureLead. Best-effort (private mode). */
export function savePassPlanToken(token: string): void {
  try {
    localStorage.setItem(PASS_PLAN_TOKEN_KEY, token);
  } catch {
    /* Safari private mode / storage disabled — the URL token still works. */
  }
}

/** The stored download token, or null when the visitor never captured here. */
export function readPassPlanToken(): string | null {
  try {
    return localStorage.getItem(PASS_PLAN_TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Where index.html's pre-analytics script parks a `?t=` it strips off the URL
 * (web #604), one key per route so a stash from one page never redeems on the other.
 */
export type PassPlanTokenRoute = 'onboarding' | 'pass-plan';
export const passPlanUrlTokenKey = (route: PassPlanTokenRoute) => `swtpa_pass_plan_url_token:${route}`;

/** The token this visit to `route` arrived with: still on the URL, or already moved to sessionStorage. */
export function readPassPlanUrlToken(
  route: PassPlanTokenRoute,
  search: string = window.location.search,
): string | null {
  const fromUrl = new URLSearchParams(search).get('t');
  if (fromUrl) return fromUrl;
  try {
    return sessionStorage.getItem(passPlanUrlTokenKey(route));
  } catch {
    return null;
  }
}

/** Forget the arrival token once it has been redeemed or persisted. */
export function clearPassPlanUrlToken(route: PassPlanTokenRoute): void {
  try {
    sessionStorage.removeItem(passPlanUrlTokenKey(route));
  } catch {
    /* storage disabled, so nothing was stored */
  }
}

/**
 * Save the gated PDF without the token ever landing in a URL or DOM attribute,
 * where Clarity's replay would record it: fetch it, then save a blob: URL.
 * 'failed' on a 5xx or network error so the page can offer a retry.
 */
export type PassPlanPdfResult = 'saved' | 'failed' | { openUrl: string };

export async function downloadPassPlanPdf(token: string): Promise<PassPlanPdfResult> {
  let res: Response;
  try {
    res = await fetch(`/api/pass-plan?t=${encodeURIComponent(token)}`);
  } catch {
    return 'failed';
  }
  // A rejected token is redirected server-side to the /try HTML page.
  if (res.redirected || res.headers.get('content-type')?.includes('html')) {
    window.location.assign(appHref('/try?src=pass_plan_locked'));
    return 'saved';
  }
  if (!res.ok || !res.headers.get('content-type')?.includes('pdf')) return 'failed';
  const url = URL.createObjectURL(await res.blob());
  // IG/FB iOS webviews ignore <a download> on a blob:. Opening it navigates the
  // tab, so the page does that on a tap, never while sign-in is in flight.
  if (isInAppBrowser()) return { openUrl: url };
  const a = document.createElement('a');
  a.href = url;
  a.download = 'ASWB-Pass-Plan-2026.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return 'saved';
}
